import { useCallback } from "react";

export const useRecordingLifecycle = (
  sessions,
  consultationService,
  busyState,
  consultationManager
) => {
  const { deleteSession, isCurrent } = sessions;
  const { begin, end } = busyState;
  const {
    fetchConsultations,
    formatLocalDateTime,
    removeProcessingPlaceholder,
  } = consultationManager;

  const stopStreamTracks = useCallback((stream) => {
    if (!stream) return;
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch {}
  }, []);

  const handleSessionReset = useCallback(
    (sessionId, session, audioURL, setAudioURL) => {
      session.resetPending = false;

      if (isCurrent(sessionId)) {
        if (audioURL) URL.revokeObjectURL(audioURL);
        setAudioURL(null);
        end("recording");
        if (session.uploadingStarted) {
          session.uploadingStarted = false;
          end("uploading");
        }
      }

      stopStreamTracks(session.stream);
      deleteSession(sessionId);
    },
    [isCurrent, end, stopStreamTracks, deleteSession]
  );

  const finalizeRecording = useCallback(
    async (
      sessionId,
      session,
      segBlob,
      setProcessingStage,
      setAudioURL,
      audioURL,
      setIsRecording,
      setIsPaused
    ) => {
      if (isCurrent(sessionId)) {
        setIsRecording(false);
        setIsPaused(false);
        end("recording");
      }

      try {
        if (!session.uploadingStarted) {
          session.uploadingStarted = true;
          if (isCurrent(sessionId)) {
            setProcessingStage("uploading");
            begin("uploading");
          } else {
            begin("uploading");
          }
        }

        if (isCurrent(sessionId)) {
          if (audioURL) URL.revokeObjectURL(audioURL);
          if (segBlob) setAudioURL(URL.createObjectURL(segBlob));
        }
      } catch (e) {
        console.error("Finalization failed:", e);
        removeProcessingPlaceholder(sessionId);
        if (isCurrent(sessionId)) {
          setProcessingStage("error");
          consultationManager.setViewMode("new");
        }
      } finally {
        if (session.uploadingStarted) {
          session.uploadingStarted = false;
          end("uploading");
        }
        stopStreamTracks(session.stream);
      }
    },
    [
      isCurrent,
      begin,
      end,
      consultationService,
      removeProcessingPlaceholder,
      stopStreamTracks,
    ]
  );

  const autoFillTemplate = useCallback(
    async (text, transcriptId, processingMode, sessionId, setProcessingStage) => {
      if (isCurrent(sessionId)) setProcessingStage("filling");
      begin("filling");

      try {
        const formattedName = formatLocalDateTime();
        const result = await consultationService.autoFillTemplate(
          text,
          transcriptId,
          formattedName,
          processingMode
        );

        let fileIds =
          result.data?.fileIds ||
          (result.data?.fileId ? [result.data.fileId] : []);

        if (
          !fileIds?.length &&
          (result.data?.task_id || result.data?.status_url)
        ) {
          const token = localStorage.getItem("access_token");
          const statusData = await consultationService.pollAutofillStatus({
            jobId: result.data.task_id,
            statusUrl: result.data.status_url,
            token,
          });
          fileIds = statusData?.fileIds || [];
        }

        if (fileIds.length) {
          removeProcessingPlaceholder(sessionId);
          if (isCurrent(sessionId)) {
            setProcessingStage("done");
            consultationManager.setViewMode("new");
          }
          await fetchConsultations();
        } else {
          removeProcessingPlaceholder(sessionId);
          if (isCurrent(sessionId)) {
            setProcessingStage("error");
            consultationManager.setViewMode("new");
          }
        }
      } catch (err) {
        console.error("Autofill error:", err);
        removeProcessingPlaceholder(sessionId);
        if (isCurrent(sessionId)) {
          setProcessingStage("error");
          consultationManager.setViewMode("new");
        }
      } finally {
        end("filling");
      }
    },
    [
      begin,
      end,
      isCurrent,
      consultationService,
      removeProcessingPlaceholder,
      fetchConsultations,
      formatLocalDateTime,
    ]
  );

  return {
    handleSessionReset,
    finalizeRecording,
    autoFillTemplate,
    stopStreamTracks,
  };
};
